import { Ref, createElement, Component } from 'react';
import type { HTMLAttributes, ComponentLifecycle } from 'react';
import ReactDom from 'react-dom';

import { attachProps, isCoveredByReact } from './attachProps';
import { createForwardRef } from './createForwardRef';
import { dashToPascalCase } from './case';

interface ReactInternalProps<ElementType> extends HTMLAttributes<ElementType> {
	forwardedRef?: Ref<ElementType>;
	ref?: Ref<ElementType>;
}

export const createReactComponent = <PropType, ElementType>(tagName: string) => {
	const displayName = dashToPascalCase(tagName);
	const ReactComponent = class
		extends Component<ReactInternalProps<PropType>>
		implements ComponentLifecycle<ReactInternalProps<PropType>, {}> {
		// eslint-disable-next-line @typescript-eslint/no-useless-constructor
		constructor(props: ReactInternalProps<PropType>) {
			super(props);
		}

		componentDidMount() {
			this.componentDidUpdate(this.props);
		}

		componentDidUpdate(prevProps: ReactInternalProps<PropType>) {
			const node = ReactDom.findDOMNode(this) as HTMLElement;
			attachProps(node, this.props, prevProps);
		}

		render() {
			const { children, forwardedRef, style, className, ref, ...cProps } = this.props;

			const propsToPass = Object.keys(cProps).reduce((acc, name) => {
				if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
					const eventName = name.substring(2).toLowerCase();
					if (isCoveredByReact(eventName)) {
						(acc as any)[name] = (cProps as any)[name];
					}
				}
				return acc;
			}, {});

			const newProps: ReactInternalProps<PropType> = {
				...propsToPass,
				ref: forwardedRef,
				style,
			};

			return createElement(tagName, newProps, children);
		}

		static get displayName() {
			return displayName;
		}
	};
	return createForwardRef<PropType, ElementType>(ReactComponent, displayName);
};
